import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { Apollo } from 'apollo-angular';
import { gql } from 'graphql-tag';
import { Subscription } from 'rxjs';

import { GetAegisDocumentLinkGQL, GetConfigValuesGQL } from '../../../../../generated/graphql.generated';
import { PandaDocService } from '../../../../features/PandaDoc/pandadoc.service';
import { selectJobFranchiseId } from '../../../job-tool.reducer';

import { NavBaseComponent } from './nav-base';

/**
 * In-navigator document signing. Named for PandaDoc because `PandaDocSigner` is the tag in the
 * **deployed** script and renaming it would mean re-registering the component in four places for no
 * behavioural gain — it drives **either** engine (#57).
 *
 * Signing links never come from the component directly: PandaDoc goes through `PandaDocService`,
 * Aegis through `getAegisDocumentLink`. Both resolve on the backend.
 *
 * Fallback chain for detecting signing — all three work on both engines, because
 * `aegis.signDocument` publishes the same `DOCUMENT_SIGNED` trigger and writes the same
 * `signedBy<Role>` metadata keys PandaDoc's webhook does:
 *  1. DocumentSigned subscription (primary)
 *  2. Artifact metadata poll on iframe close
 *  3. Manual "Mark as Signed" override
 */

const CREATE_DOCUMENT = gql`
mutation NavCreateDocument(
    $serviceToUse: String!,
    $documentsToSend: [String!],
    $saveArtifact: Boolean,
    $commentWithArtifactOn: [String],
    $send: Boolean,
    $variables: DocumentVariablesInput,
) {
    createDocument(
        serviceToUse: $serviceToUse,
        documentsToSend: $documentsToSend,
        saveArtifact: $saveArtifact,
        commentWithArtifactOn: $commentWithArtifactOn,
        send: $send,
        variables: $variables,
    ) {
        artifact { id attributes metadata }
        recipients { email firstName lastName role }
    }
}
`;

const GET_ARTIFACT_STATUS = gql`
query NavGetArtifactStatus($artifactId: String!) {
    artifacts(filter: { ids: [$artifactId] }) {
        artifacts { id metadata attributes }
    }
}
`;

/**
 * The second copy of the same lookup `nav-pregen-doc.component.ts` uses, and it had the same defect
 * (#41/#42): `hasAllAttributes: ["pandadoc", $docTypeName]` matched nothing, because artifacts are
 * tagged with the docType's per-zone **display name** while the script passes the slug.
 *
 * Consequences here were distinct from the duplicate-generation half. This component reads
 * `signedRoles` off the artifact it finds, so a contract that was already generated — and possibly
 * already signed — was invisible: the step always offered "Generate", and an existing signature
 * could not satisfy the gate.
 *
 * Now filters on `documentTypes`, the canonical config-key slug written by both engines. See the
 * fuller note on the pre-gen component.
 */
const FIND_EXISTING_ARTIFACT = gql`
query NavFindExistingArtifact($jobId: String!, $docType: String!) {
    artifacts(
        filter: {
            relatedObjectIds: [$jobId],
            relatedObjectLabels: ["Job"],
            documentTypes: [$docType]
        },
        sortBy: "createdAt_DESC",
        limit: 1
    ) {
        artifacts { id attributes metadata }
    }
}
`;

const DOCUMENT_SIGNED_SUB = gql`
subscription NavDocumentSigned($jobId: String!) {
    documentSigned(jobId: $jobId) {
        artifactId
        signedByRole
    }
}
`;

type SignerRole = 'employee' | 'customer' | 'driver' | 'finalClient';

type SigningState = 'idle' | 'checking' | 'generating' | 'ready' | 'signing' | 'complete';

type DocumentEngine = 'aegis' | 'pandadoc';

/**
 * Aegis accepts only these two signer roles (`AEGIS_SIGNER_ROLES`), and
 * `getAegisDocumentLink` validates `role` against that closed set — a role outside it is rejected
 * at the boundary rather than minting a useless token. `driver` and `finalClient` exist for
 * PandaDoc only, so an Aegis document declaring them is a script error worth reporting rather than
 * a request worth sending.
 */
const AEGIS_SIGNER_ROLES: readonly SignerRole[] = ['customer', 'employee'];

/**
 * Rank for the never-downgrade rule in `_applyArtifactData`, spanning both engines' vocabularies.
 * PandaDoc's states run draft → completed; Aegis contributes `partially-signed` and `signed`.
 *
 * `signed` sits below `completed` rather than beside it because the two are not synonyms: Aegis
 * `signed` means every declared signer has signed, while PandaDoc `completed` is a document-level
 * state that can also arrive without per-role webhooks. Both are treated as "everyone signed" by
 * the check in `_applyArtifactData`; only the ordering differs.
 */
const STATUS_ORDER = [
    'draft',
    'uploaded',
    'sent',
    'viewed',
    'waiting_approval',
    'approved',
    'partially-signed',
    'signed',
    'completed',
];

/** Doc-level statuses that mean every signer role is done, whichever engine wrote them. */
const FULLY_SIGNED_STATUSES = ['signed', 'completed'];

interface ArtifactCapabilities {
    hasEmployee: boolean;
    hasCustomer: boolean;
    hasDriver: boolean;
    hasFinalClient: boolean;
}

interface StepViewModel {
    role: SignerRole;
    label: string;
    hasRecipient: boolean;
    signed: boolean;
    canSign: boolean;
}

@Component({
    selector: 'app-nav-pandadoc-signer',
    standalone: true,
    imports: [CommonModule],
    styles: [`
        :host { display: block; margin-bottom: 1rem; }

        .signer-card {
            background: white; border-radius: 10px;
            box-shadow: 0 1px 4px rgba(0,0,0,0.07); overflow: hidden;
        }
        .signer-header {
            background: #1a1a1a; color: white; padding: 0.85rem 1.1rem;
        }
        .signer-title {
            font-size: 0.85rem; font-weight: 700;
            text-transform: uppercase; letter-spacing: 0.06em;
        }
        .signer-body { padding: 1rem 1.1rem; }

        .generate-desc { font-size: 0.9rem; color: #555; margin-bottom: 1rem; line-height: 1.5; }
        .generate-btn {
            display: flex; align-items: center; justify-content: center; gap: 0.5rem;
            width: 100%; padding: 0.9rem; background: #1a1a1a; color: white;
            border: none; border-radius: 8px; font-size: 1rem; font-weight: 600;
            cursor: pointer; font-family: inherit;
        }
        .generate-btn:active { background: #000; }
        .generate-btn:disabled { background: #888; cursor: not-allowed; }

        .spinner-row {
            display: flex; align-items: center; gap: 0.75rem;
            padding: 0.5rem 0; color: #555; font-size: 0.9rem;
        }

        .sign-steps { display: flex; flex-direction: column; }
        .sign-step {
            display: flex; align-items: center; gap: 0.85rem;
            padding: 0.75rem 0; border-bottom: 1px solid #f0f0f0;
            /* Wraps so the override link and its confirmation take their own line instead of
               competing with the label for horizontal space — see the 100% basis below. */
            flex-wrap: wrap;
        }
        .sign-step:last-child { border-bottom: none; }

        .step-icon {
            width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
            display: flex; align-items: center; justify-content: center; font-size: 0.88rem;
        }
        .step-icon.done    { background: #2d6a2d; color: white; }
        .step-icon.pending { background: #f0f0f0; color: #888; }

        .step-info { flex: 1; min-width: 0; }
        .step-label { font-size: 0.95rem; font-weight: 600; color: #1a1a1a; }
        .step-sub { font-size: 0.78rem; color: #888; margin-top: 0.1rem; }
        .step-sub.green { color: #2d6a2d; }
        .step-sub.amber { color: #d97706; }

        .sign-btn {
            padding: 0.5rem 1rem; background: #2d6a2d; color: white;
            border: none; border-radius: 8px; font-size: 0.88rem; font-weight: 600;
            cursor: pointer; font-family: inherit; white-space: nowrap;
            display: flex; align-items: center; gap: 0.35rem;
        }
        .sign-btn:active { background: #1f4d1f; }
        .sign-btn:disabled { background: #b0b8b0; cursor: not-allowed; }
        .sign-btn.loading { background: #888; cursor: not-allowed; }
        .sign-btn.open { background: #555; }

        .mark-signed-btn {
            min-height: 44px; padding: 0 0.85rem; background: none;
            border: 1.5px solid #2d6a2d; color: #2d6a2d;
            border-radius: 8px; font-size: 0.82rem; font-weight: 600;
            cursor: pointer; font-family: inherit; white-space: nowrap; margin-left: 0.4rem;
        }
        .mark-signed-btn:active { background: #f0f7f0; }

        /* Override affordance — amber, never green. It records an assertion, not a signature. */
        .override-link {
            /* Own line: a flex child of .sign-step, which wraps. Without the 100% basis this sits
               beside the step label and squeezes it on a phone. (No backticks in CSS comments —
               this block is inside a template literal.) */
            flex: 0 0 100%;
            display: block; margin-top: 0.5rem; padding: 0.2rem 0; text-align: left;
            background: none; border: none; cursor: pointer; font-family: inherit;
            font-size: 0.78rem; color: #a16207; text-decoration: underline;
        }
        .override-confirm {
            flex: 0 0 100%;
            margin-top: 0.5rem; padding: 0.7rem 0.85rem;
            background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px;
            font-size: 0.82rem; color: #78350f; line-height: 1.45;
        }
        .override-actions { display: flex; gap: 0.5rem; margin-top: 0.6rem; }
        .override-actions button {
            flex: 1; padding: 0.55rem 0.5rem; border-radius: 8px;
            font-size: 0.82rem; font-weight: 700; cursor: pointer; font-family: inherit;
        }
        .override-cancel { background: white; border: 1.5px solid #d6d3d1; color: #44403c; }
        .override-accept { background: #a16207; border: 1.5px solid #a16207; color: white; }
        .override-badge {
            display: inline-block; margin-left: 0.4rem; padding: 0.15rem 0.5rem;
            background: #fef3c7; color: #92400e; border-radius: 10px;
            font-size: 0.7rem; font-weight: 700; white-space: nowrap;
        }

        .handoff-card {
            background: #fff7ed; border: 1px solid #fed7aa;
            border-radius: 8px; padding: 0.75rem 1rem; margin: 0.5rem 0;
            font-size: 0.88rem; color: #9a3412; line-height: 1.5;
            display: flex; align-items: center; gap: 0.6rem;
        }

        .checking-row {
            display: flex; align-items: center; gap: 0.5rem;
            font-size: 0.82rem; color: #888; margin-top: 0.5rem;
        }

        .complete-banner {
            display: flex; align-items: center; gap: 0.75rem;
            background: #f0f7f0; border-radius: 8px; padding: 1rem;
            color: #2d6a2d; font-weight: 600;
        }
        .complete-banner i { font-size: 1.3rem; }

        .skip-row {
            padding: 0.5rem 1.1rem 0.85rem;
            display: flex; align-items: center; gap: 0.5rem;
        }
        /* Outlined and grey (#100b): a real 44px button, but still the quietest thing on the
           step — deferring the contract should not look like the way forward. */
        .skip-btn {
            min-height: 44px; padding: 0 0.9rem; border-radius: 8px;
            background: white; border: 1.5px solid #ccc; color: #666;
            font-size: 0.85rem; font-weight: 600; font-family: inherit; cursor: pointer;
        }
        .skip-btn:active { background: #f0f0f0; }
        .skip-badge {
            background: #fff7ed; color: #d97706;
            font-size: 0.75rem; font-weight: 600; padding: 0.2rem 0.55rem; border-radius: 10px;
        }
        .error-text { color: #dc2626; font-size: 0.85rem; margin-top: 0.5rem; }

        /* Full-screen iframe overlay */
        .iframe-overlay {
            position: fixed; inset: 0; z-index: 2000;
            display: flex; flex-direction: column; background: white;
        }
        .iframe-topbar {
            display: flex; align-items: center; justify-content: space-between;
            padding: 0.75rem 1rem; background: #1a1a1a; color: white; flex-shrink: 0;
        }
        .iframe-topbar-title { font-size: 0.95rem; font-weight: 600; }
        .iframe-close-btn {
            background: rgba(255,255,255,0.15); border: none; color: white;
            padding: 0.4rem 0.85rem; border-radius: 6px;
            cursor: pointer; font-family: inherit; font-size: 0.88rem;
            display: flex; align-items: center; gap: 0.4rem;
        }
        .iframe-close-btn:active { background: rgba(255,255,255,0.3); }
        .iframe-loading {
            flex: 1; display: flex; flex-direction: column;
            align-items: center; justify-content: center; gap: 1.25rem;
            background: #f5f7fa; text-align: center; padding: 2rem;
        }
        .iframe-loading i { font-size: 2.5rem; color: #2d6a2d; }
        .iframe-loading-title { font-size: 1.1rem; font-weight: 700; color: #1a1a1a; }
        .iframe-loading-sub { font-size: 0.9rem; color: #888; }
        iframe { flex: 1; border: none; width: 100%; }
    `],
    template: `
        <div class="signer-card">
            <div class="signer-header">
                <span class="signer-title">
                    <i class="pi pi-file-pdf" style="margin-right:0.4rem;"></i>
                    {{ node.props?.title || 'Document Signing' }}
                </span>
            </div>
            <div class="signer-body">

                <!-- IDLE -->
                <ng-container *ngIf="state === 'idle'">
                    <p class="generate-desc">
                        {{ node.props?.subtitle || 'Generate the document to proceed.' }}
                    </p>
                    <button class="generate-btn" (click)="generate()" [disabled]="generating">
                        <i class="pi pi-file-edit"></i> Generate Document
                    </button>
                    <p class="error-text" *ngIf="error">{{ error }}</p>
                </ng-container>

                <!-- CHECKING / GENERATING -->
                <ng-container *ngIf="state === 'checking' || state === 'generating'">
                    <div class="spinner-row">
                        <i class="pi pi-spin pi-spinner" style="font-size:1.1rem;color:#2d6a2d;"></i>
                        {{ state === 'generating' ? 'Generating document...' : 'Looking for existing document...' }}
                    </div>
                </ng-container>

                <!-- READY / SIGNING -->
                <ng-container *ngIf="state === 'ready' || state === 'signing'">
                    <div class="sign-steps">
                        <div class="sign-step" *ngFor="let step of steps; trackBy: trackByRole">

                            <div class="step-icon" [class.done]="step.signed" [class.pending]="!step.signed">
                                <i [class]="step.signed ? 'pi pi-check' : 'pi pi-pencil'"></i>
                            </div>

                            <div class="step-info">
                                <div class="step-label">
                                    {{ step.label }}
                                    <span class="override-badge" *ngIf="manuallyConfirmedRoles.has(step.role)">
                                        confirmed by crew
                                    </span>
                                </div>
                                <div class="step-sub green" *ngIf="step.signed">Signed ✓</div>
                                <div class="step-sub amber" *ngIf="!step.signed && !step.canSign">
                                    Waiting for {{ prevLabel(step.role) }}
                                </div>
                                <div class="step-sub" *ngIf="!step.signed && step.canSign">
                                    Ready to sign
                                </div>
                            </div>

                            <ng-container *ngIf="!step.signed">

                                <!-- Sign Now — has a real recipient configured -->
                                <ng-container *ngIf="step.hasRecipient">
                                    <button class="sign-btn"
                                        [class.loading]="loadingLink && activeRole === step.role"
                                        [disabled]="!step.canSign || loadingLink"
                                        (click)="openIframe(step.role)">
                                        <i *ngIf="loadingLink && activeRole === step.role"
                                           class="pi pi-spin pi-spinner"></i>
                                        {{ loadingLink && activeRole === step.role ? 'Loading...' : 'Sign Now' }}
                                    </button>
                                    <!-- Re-query the real signature status. NOT a manual override:
                                         this role has a signature field, so the only thing that may
                                         satisfy it is the engine actually reporting it signed. -->
                                    <button class="mark-signed-btn"
                                        *ngIf="(docStatus !== 'draft' || signingOpened) && step.canSign"
                                        [disabled]="pollingStatus"
                                        (click)="recheckSignature(step.role)"
                                        title="Re-check whether the signature has landed">
                                        <i *ngIf="pollingStatus" class="pi pi-spin pi-spinner"></i>
                                        {{ pollingStatus ? 'Checking...' : '↻ Check again' }}
                                    </button>

                                    <!-- Override, offered only once a re-check has run and come back
                                         without the signature. Deliberately understated and amber:
                                         it records what the crew asserts, not what the engine saw. -->
                                    <button class="override-link"
                                        *ngIf="canOfferOverride(step.role) && overrideConfirmingRole !== step.role"
                                        (click)="askOverride(step.role)">
                                        Signature not detected — confirm it was signed
                                    </button>

                                    <!-- Requirement: [6750] - no vendor name in crew-facing copy. This
                                         step signs through whichever engine produced the document,
                                         and since #64 the navigator only ever creates Aegis ones —
                                         so naming PandaDoc here told the crew about a product that
                                         had nothing to do with the document in front of them. -->
                                    <div class="override-confirm" *ngIf="overrideConfirmingRole === step.role">
                                        <div>
                                            No signature has been recorded for
                                            <strong>{{ roleLabel(step.role) }}</strong>.
                                            Only continue if they have actually signed — you are
                                            recording this on their behalf.
                                        </div>
                                        <div class="override-actions">
                                            <button class="override-cancel" (click)="cancelOverride()">Cancel</button>
                                            <button class="override-accept" (click)="confirmOverride(step.role)">
                                                Yes, they signed
                                            </button>
                                        </div>
                                    </div>
                                </ng-container>

                                <!-- Open + Done — no formal signature field on this doc -->
                                <ng-container *ngIf="!step.hasRecipient && step.canSign">
                                    <button class="sign-btn open"
                                        [class.loading]="loadingLink && activeRole === step.role"
                                        [disabled]="loadingLink"
                                        (click)="openIframe(step.role)">
                                        <i *ngIf="loadingLink && activeRole === step.role"
                                           class="pi pi-spin pi-spinner"></i>
                                        {{ loadingLink && activeRole === step.role ? 'Loading...' : 'Open Doc' }}
                                    </button>
                                    <!-- Legitimate: this role has no signature field on the document,
                                         so there is nothing for either engine to report. "Reviewed",
                                         not "Signed" — the label should not imply a signature. -->
                                    <button class="mark-signed-btn" (click)="markSigned(step.role)">
                                        ✓ Reviewed
                                    </button>
                                </ng-container>

                            </ng-container>

                        </div>
                    </div>

                    <div class="handoff-card" *ngIf="showHandoffPrompt">
                        <i class="pi pi-hand-pointer"></i>
                        <span><strong>Hand the device to the customer</strong> — they need to sign next.</span>
                    </div>

                    <div class="checking-row" *ngIf="pollingStatus">
                        <i class="pi pi-spin pi-spinner"></i>
                        Checking signing status...
                    </div>

                    <p class="error-text" *ngIf="error">{{ error }}</p>
                </ng-container>

                <!-- COMPLETE -->
                <ng-container *ngIf="state === 'complete'">
                    <div class="complete-banner">
                        <i class="pi pi-check-circle"></i>
                        {{ completeMessage }}
                    </div>
                </ng-container>

            </div>

            <div class="skip-row" *ngIf="node.props?.optional && state !== 'complete'">
                <span class="skip-badge" *ngIf="skipped">⚠ Skipped</span>
                <button class="skip-btn" *ngIf="!skipped" (click)="skip()">Sign later</button>
            </div>
        </div>

        <!-- Full-screen iframe overlay -->
        <div class="iframe-overlay" *ngIf="iframeOpen">
            <div class="iframe-topbar">
                <span class="iframe-topbar-title">{{ iframeTitle }}</span>
                <button class="iframe-close-btn" (click)="closeIframe()">
                    <i class="pi pi-times"></i> Done / Close
                </button>
            </div>

            <div class="iframe-loading" *ngIf="!signingUrl">
                <i class="pi pi-spin pi-spinner"></i>
                <div>
                    <div class="iframe-loading-title">Opening document...</div>
                    <div class="iframe-loading-sub">Getting your signing link from the server</div>
                </div>
            </div>

            <ng-container *ngIf="signingUrl">
                <div class="iframe-loading" *ngIf="iframeLoading"
                     style="position:absolute;top:48px;left:0;right:0;bottom:0;z-index:1;">
                    <i class="pi pi-spin pi-spinner"></i>
                    <div>
                        <div class="iframe-loading-title">Loading document...</div>
                        <div class="iframe-loading-sub">Preparing the document for signing</div>
                    </div>
                </div>
                <iframe [src]="signingUrl"
                    allow="camera; microphone"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                    (load)="iframeLoading = false"
                    style="position:relative;z-index:2;">
                </iframe>
            </ng-container>
        </div>
    `,
})
export class NavPandaDocSignerComponent extends NavBaseComponent implements OnInit, OnDestroy {

    // ── State ─────────────────────────────────────────────────────────────────
    state: SigningState = 'idle';
    generating = false;
    error = '';
    skipped = false;
    completeMessage = 'Document signed';
    /**
     * Document-level status from artifact attributes. PandaDoc writes
     * 'draft'|'sent'|'viewed'|…|'completed' from its webhook; Aegis writes
     * 'partially-signed'|'signed' from `aegis.signDocument`. Both vocabularies are ranked in
     * `STATUS_ORDER` so the never-downgrade rule holds across engines.
     */
    docStatus = 'draft';

    artifactId = '';
    signedRoles = new Set<string>();
    // Default true — assume all roles have recipients until proven otherwise.
    // If getPandadocDocumentLink throws "no recipient", we flip the flag.
    capabilities: ArtifactCapabilities = { hasEmployee: true, hasCustomer: true, hasDriver: true, hasFinalClient: true };

    iframeOpen = false;
    iframeLoading = false;
    loadingLink = false;
    pollingStatus = false;

    /**
     * Whether a signing surface has been opened at least once this session — i.e. whether a
     * signature could plausibly have landed yet. Gates "↻ Check again", and through it the manual
     * override, which must never be reachable before the crew has actually tried to sign.
     *
     * `docStatus !== 'draft'` used to carry that meaning on its own, but only because
     * `getPandadocDocumentLink` sends the document as a side effect and the old code stamped
     * 'sent' locally. Aegis mints its link without sending, so an Aegis document is still a draft
     * after the crew has signed in it — leaving the re-check, and any recovery from a signature
     * that failed to register, permanently unreachable. This restores that path without pretending
     * the document was sent. No behaviour change for PandaDoc, where the status has already moved
     * off 'draft' by the time this is true.
     */
    signingOpened = false;

    /** Roles for which a re-check has run and come back without a signature. Unlocks the override. */
    recheckedWithoutSignature = new Set<SignerRole>();
    /** The role whose override confirmation is currently showing, if any. */
    overrideConfirmingRole: SignerRole | null = null;
    /** Roles the crew asserted rather than the engine reporting. Surfaced as a badge — see #54. */
    manuallyConfirmedRoles = new Set<SignerRole>();
    activeRole: SignerRole = 'employee';
    signingUrl: SafeResourceUrl | null = null;

    /** Stable step list — rebuilt only when _rebuildSteps() is called */
    steps: StepViewModel[] = [];

    /**
     * Which engine produced the artifact we are acting on — **not** which engine the zone is
     * configured for today. The distinction is the whole point (#57): a document created under one
     * engine must keep being read through that engine, or a contract generated before a config flip
     * gets its signing link minted by the wrong service. `invoices.update` branches the same way.
     *
     * Read from the artifact's own `aegis` attribute tag. The node also carries a dedicated
     * `documentEngine` property, which would be the better signal, but it is not exposed on the
     * GraphQL `Artifact` type — adding it is a shared-schema change, deliberately not bundled here.
     * `aegis.createDocument` writes the tag and that property in the same statement, so the tag is
     * equally reliable.
     */
    artifactEngine: DocumentEngine = 'pandadoc';

    private sub: Subscription | null = null;

    /**
     * Whether this zone's config has already been checked for this docType (#72).
     *
     * Only a *pass* is remembered. A failure must not be, or a zone fixed mid-step would keep
     * reporting the old error at the crew until they leave and come back.
     */
    private aegisConfigChecked = false;

    private readonly aegisLinkGQL = inject(GetAegisDocumentLinkGQL);
    private readonly configGQL = inject(GetConfigValuesGQL);
    private readonly store = inject(Store);

    /**
     * The zone every document call must be made **against**, which is not the zone the app happens
     * to be viewing and not always the job's own zone either.
     *
     * A job's zone is frequently an `area` — this test job sits in "KC- Jackson County MO" — while
     * `standard-documents.*` is configured one level up on the franchise. `selectJobFranchiseId`
     * encodes exactly that hop (area → parent, otherwise the zone itself), and it is the zone the
     * backend itself stamps on the artifact.
     *
     * Sending nothing resolves the config against the app's current zone. That is not a hypothetical
     * mismatch — `selectJobInCurrentZone` exists because the two routinely differ — and the failure
     * is silent in the worst way: the brand root carries the same `standard-documents.moving-contract`
     * key *without* an `engine`, so the read succeeds and reports 'pandadoc'. A crew would get a
     * PandaDoc contract in an Aegis franchise with nothing logged.
     */
    private readonly documentZoneId = this.store.selectSignal(selectJobFranchiseId);

    /**
     * Apollo context pinning a request to the job's franchise, or `{}` when the zone is not known
     * yet. Omitting the header falls back to the app's current zone, which is wrong but survivable;
     * sending `x-zone: undefined` asks the backend to resolve a zone literally named "undefined".
     *
     * Needed by every **artifact read** here, not just the config read. `artifacts.list` filters by
     * zone with `inheritZoneFromJob`, and with no explicit `zoneDir` it defaults to `lte` —
     * `$zone IN z.parents OR $zone = z.id` — so the caller must be the job's zone or an ancestor of
     * it. The franchise is an ancestor of the job's area zone, so it always matches; the app's
     * current zone need not be related to the job at all. Every failure is silent: an invisible
     * artifact reads as "no contract exists" (→ a duplicate document) or as "not signed yet"
     * (→ a gate that never opens).
     */
    private _zoneContext(): { headers?: Record<string, string> } {
        const zone = this.documentZoneId();
        return zone ? { headers: { 'x-zone': zone } } : {};
    }

    constructor(
        private apollo: Apollo,
        private sanitizer: DomSanitizer,
        private pandadocService: PandaDocService,
    ) { super(); }

    // ── Computed ──────────────────────────────────────────────────────────────

    get signerRoles(): SignerRole[] {
        return (this.node.props?.signerRoles ?? ['employee', 'customer']) as SignerRole[];
    }

    get showHandoffPrompt(): boolean {
        return this.signedRoles.has('employee')
            && !this.signedRoles.has('customer')
            && this.signerRoles.includes('customer');
    }

    get iframeTitle(): string {
        const hasRec = this._roleHasRecipient(this.activeRole);
        return `${hasRec ? 'Sign as' : 'Review —'} ${this.roleLabel(this.activeRole)}`;
    }

    get isSatisfied(): boolean {
        if (this.node.props?.optional && this.skipped) return true;
        if (this.state === 'complete') return true;
        return this.signerRoles.every(r => this.signedRoles.has(r));
    }

    trackByRole(_: number, step: StepViewModel): string { return step.role; }

    // ── Step list ─────────────────────────────────────────────────────────────

    private _rebuildSteps() {
        const roles = this.signerRoles;
        const steps: StepViewModel[] = [];
        for (let i = 0; i < roles.length; i++) {
            const role = roles[i];
            const signed = this.signedRoles.has(role);
            const prevDone = i === 0 || steps[i - 1].signed || !steps[i - 1].hasRecipient;
            steps.push({
                role,
                label: this.roleLabel(role),
                hasRecipient: this._roleHasRecipient(role),
                signed,
                canSign: prevDone && !signed,
            });
        }
        this.steps = steps;
    }

    // ── Role helpers ──────────────────────────────────────────────────────────

    roleLabel(role: string): string {
        return ({ employee: 'You (Employee)', customer: 'Customer', driver: 'Driver', finalClient: 'Final Client' } as any)[role]
            ?? role.charAt(0).toUpperCase() + role.slice(1);
    }

    prevLabel(role: string): string {
        const idx = this.signerRoles.indexOf(role as SignerRole);
        return idx > 0 ? this.roleLabel(this.signerRoles[idx - 1]) : '';
    }

    private _roleHasRecipient(role: SignerRole): boolean {
        const map: Record<SignerRole, boolean> = {
            employee: this.capabilities.hasEmployee,
            customer: this.capabilities.hasCustomer,
            driver: this.capabilities.hasDriver,
            finalClient: this.capabilities.hasFinalClient,
        };
        return map[role] ?? true;
    }

    private _mapWebhookRole(raw: string): SignerRole | null {
        const lower = (raw ?? '').toLowerCase();
        if (lower === 'employee' || lower === 'signer') return 'employee';
        if (lower === 'client' || lower === 'customer') return 'customer';
        if (lower === 'driver') return 'driver';
        if (lower === 'finalclient' || lower === 'final client') return 'finalClient';
        return lower as SignerRole;
    }

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    ngOnInit() {
        const jobId: string = this.node.props?.jobId ?? '';

        // Subscribe to document signing events (primary path)
        if (jobId) {
            this.sub = this.apollo.subscribe<any>({
                query: DOCUMENT_SIGNED_SUB,
                variables: { jobId },
            }).subscribe(res => {
                const data = res?.data?.documentSigned;
                if (!data || data.artifactId !== this.artifactId) return;
                const mapped = this._mapWebhookRole(data.signedByRole);
                if (mapped) this.signedRoles.add(mapped);
                this._checkComplete();
            });
        }

        // The `existingArtifactId` prop and its short-circuit were removed with `PreGenerateDoc`
        // (#58). Nothing could set it once pregen was gone, and while it was set it took precedence
        // over the lookup below — which is how a PandaDoc contract kept appearing in an Aegis
        // franchise: the pregen component hardcoded `serviceToUse: 'pandadoc'`, and the signer
        // trusted its artifact id without ever reaching `generate()`. It also failed badly when the
        // artifact was deleted but the id was not, leaving the step `ready` with a dead reference.
        // Discovery is now always by lookup, which reads the artifact that actually exists.

        const docType: string = this.node.props?.docType ?? '';
        if (jobId && docType) {
            this.state = 'checking';
            this._rebuildSteps();
            this._checkForExistingArtifact(jobId, docType);
        } else {
            this._rebuildSteps();
        }
    }

    ngOnDestroy() { this.sub?.unsubscribe(); }

    // ── Artifact loading ──────────────────────────────────────────────────────

    private async _checkForExistingArtifact(jobId: string, docType: string) {
        try {
            const res = await this.apollo.query<any>({
                query: FIND_EXISTING_ARTIFACT,
                variables: { jobId, docType },
                fetchPolicy: 'no-cache',
                context: this._zoneContext(),
            }).toPromise();
            const existing = res?.data?.artifacts?.artifacts?.[0];
            if (existing?.id) {
                this.artifactId = existing.id;
                this._applyArtifactData(existing);
                this.state = 'ready';
                this._checkComplete();
            } else {
                this.state = 'idle';
            }
        } catch {
            this.state = 'idle';
        }
        this._rebuildSteps();
    }

    private async _loadArtifactData(artifactId: string) {
        try {
            const res = await this.apollo.query<any>({
                query: GET_ARTIFACT_STATUS,
                variables: { artifactId },
                fetchPolicy: 'no-cache',
                context: this._zoneContext(),
            }).toPromise();
            const artifact = res?.data?.artifacts?.artifacts?.[0];
            if (artifact) { this._applyArtifactData(artifact); this._checkComplete(); }
        } catch { /* non-fatal */ }
        this._rebuildSteps();
    }

    private _applyArtifactData(artifact: { id: string; metadata?: any; attributes?: string[] }) {
        const meta = artifact.metadata ?? {};
        const attrs: string[] = artifact.attributes ?? [];

        // Which engine owns this document. Set before anything else reads it, and only ever from
        // the artifact — see the field's comment for why the zone config must not be consulted here.
        //
        // Only reassign when the artifact actually told us something. `attributes` is nullable in
        // the schema, and this method runs again on every poll, so treating an empty list as
        // 'pandadoc' would let a later read silently overwrite a correct earlier one — routing an
        // Aegis document through PandaDoc's link service, and failing outside the `noRecipient`
        // recovery because that branch is PandaDoc-gated. Same reasoning as the never-downgrade
        // rule for `docStatus` below; engine matters more, since it selects the backend service.
        //
        // A latch would be wrong here: `generate()` assigns a new `artifactId` to this same
        // instance, so the engine has to stay re-derivable when a different artifact arrives.
        if (attrs.length) {
            this.artifactEngine = attrs.includes('aegis') ? 'aegis' : 'pandadoc';
        }

        // Read doc-level status from attributes (PandaDoc: document_state_changed webhook;
        // Aegis: aegis.signDocument / aegis.sendDocument).
        // Never downgrade — if we already know the doc was sent (from getDocumentLink),
        // don't let a stale artifact attribute overwrite it back to draft.
        const statusAttr = attrs.find(a => a.startsWith('status::'));
        const newStatus = statusAttr ? statusAttr.replace('status::', '') : 'draft';
        const currentRank = STATUS_ORDER.indexOf(this.docStatus);
        const newRank = STATUS_ORDER.indexOf(newStatus);
        if (newRank > currentRank) this.docStatus = newStatus;
        // If not in our list, always accept it
        if (newRank === -1) this.docStatus = newStatus;

        // null = explicit no-recipient; missing key = assume capable
        this.capabilities = {
            hasEmployee:    'employeeRecipientId' in meta ? !!meta.employeeRecipientId : true,
            hasCustomer:    'customerRecipientId' in meta ? !!meta.customerRecipientId : true,
            hasDriver:      true,
            hasFinalClient: true,
        };

        // Restore signatures from webhook-set metadata
        this._applyMetadataSignatures(meta);

        // If the whole document is done, mark all roles as signed. `FULLY_SIGNED_STATUSES` covers
        // both engines: PandaDoc's 'completed' and Aegis's 'signed' (written only once every
        // declared signer has signed — aegis.signDocument's `complete` branch).
        if (attrs.includes('document.completed') || FULLY_SIGNED_STATUSES.includes(this.docStatus)) {
            for (const r of this.signerRoles) this.signedRoles.add(r);
            return;
        }

        // For roles without a formal signature field (no recipientId), if the document
        // has at least been sent/viewed, treat that role as done — they filled their
        // textbox and completed their portion. The webhook won't fire recipient_completed
        // for textbox-only recipients.
        const docInteracted = ['sent', 'viewed', 'waiting_approval', 'approved', 'completed']
            .includes(this.docStatus);

        if (docInteracted) {
            for (const r of this.signerRoles) {
                if (!this._roleHasRecipient(r) && !this.signedRoles.has(r)) {
                    // No formal signature → auto-mark as done once doc has been seen
                    this.signedRoles.add(r);
                }
            }
        }
    }

    private _applyMetadataSignatures(meta: Record<string, any>) {
        if (meta.signedByEmployee)    this.signedRoles.add('employee');
        if (meta.signedByCustomer)    this.signedRoles.add('customer');
        if (meta.signedByDriver)      this.signedRoles.add('driver');
        if (meta.signedByFinalClient) this.signedRoles.add('finalClient');
    }

    // ── Generation ────────────────────────────────────────────────────────────

    /**
     * Refuses to generate unless this zone can actually render an Aegis document (**#72**).
     *
     * **Why this replaced an engine lookup (#64).** Creation used to read
     * `standard-documents.<docType>.engine` and dispatch whichever engine it named. The navigator
     * no longer offers PandaDoc at all, so there is nothing to choose — but the same config read is
     * still worth making, because the thing that *can* fail is now legible in advance.
     *
     * **What actually has to be true is `templateUrl`, not `engine`.** Aegis dispatch re-reads this
     * key server-side and hands `templateUrl` to `resolveTemplateUrl`, which throws on anything that
     * is not a full http/https URL. A zone configured for PandaDoc typically has no `templateUrl` at
     * all, so it fails — and it failed before this too, just less usefully: unconfigured zones used
     * to resolve to `pandadoc` and hit a **403** from `createPandadocDocument`, which is gated off
     * by default since 2026-08-31 because each PandaDoc document costs about $2. Either way the
     * crew saw a raw backend error mid-move. `engine` itself is deliberately NOT checked: a zone
     * still marked `pandadoc` but carrying a `templateUrl` can render perfectly well, and refusing
     * it would block the very migration this change is part of.
     *
     * **Only a pass is cached.** Caching a failure would keep reporting a stale error to a crew
     * whose zone was fixed while they stood in the driveway.
     *
     * @throws a message naming the missing key and the zone, so whoever reads it over the phone can
     *         act on it. Both live navigator franchises pass; this is for the next zone switched on.
     */
    private async _assertAegisConfigured(docType: string): Promise<void> {
        if (this.aegisConfigChecked) { return; }

        const key = `standard-documents.${docType}`;
        const zone = this.documentZoneId() ?? 'this zone';
        const askTheOffice = `Ask the office to set \`${key}.templateUrl\` for ${zone}.`;

        let config: { key?: string | null; value?: string | null } | undefined;
        try {
            const res = await this.configGQL.fetch(
                { keys: [key] },
                { fetchPolicy: 'no-cache', context: this._zoneContext() },
            ).toPromise();
            config = (res?.data?.getConfigValues ?? []).find((c) => c?.key === key) ?? undefined;
        } catch (err: any) {
            // A read that errored is NOT a missing config, and must not be reported as one — the
            // office would go looking for a key that is present. Say what actually happened.
            console.warn('[NavPandaDocSigner] document config read failed', err?.message ?? err);
            throw new Error('Could not check the document settings for this zone. Check your connection and try again.');
        }

        // An absent row is not "not configured" either: `config.view` filters keys out of the
        // response silently rather than erroring, and the read is made against the franchise
        // rather than the job's own zone. Both look identical from here, so the message names the
        // zone it looked in.
        if (!config?.value) {
            throw new Error(`No document settings found for "${docType}" in ${zone}. ${askTheOffice}`);
        }

        let templateUrl: string | undefined;
        try {
            templateUrl = JSON.parse(config.value)?.templateUrl;
        } catch {
            throw new Error(`The document settings for "${docType}" in ${zone} are not readable. ${askTheOffice}`);
        }

        if (typeof templateUrl !== 'string' || !templateUrl.trim()) {
            throw new Error(`"${docType}" has no document template configured in ${zone}. ${askTheOffice}`);
        }

        // A truthy value is not a usable one. `resolveTemplateUrl` (`aegis.templates.ts`) parses it
        // with `new URL()` and rejects anything relative, so a config holding
        // `/functions/templates/...` reached the crew as the raw
        // `Invalid templateUrl (must be a full http/https URL)` — the exact failure this method
        // exists to pre-empt (#72). Relative paths are the likely misconfiguration, not a
        // hypothetical one: two repo docs recommended that spelling until this MR corrected them.
        //
        // This narrows the gap rather than closing it. The backend also checks the host against
        // `cfg.aegis.templates.allowedHosts`, which is not readable from here, so a well-formed URL
        // pointing at a disallowed host still fails server-side.
        let parsed: URL;
        try {
            parsed = new URL(templateUrl);
        } catch {
            throw new Error(`"${docType}" has an invalid document template URL in ${zone} — it must be a full `
                + `web address. ${askTheOffice}`);
        }

        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            throw new Error(`"${docType}" has a document template URL in ${zone} that is not http(s). ${askTheOffice}`);
        }

        this.aegisConfigChecked = true;
    }

    async generate() {
        this.generating = true;
        this.state = 'generating';
        this.error = '';

        const docType: string = this.node.props?.docType ?? '';
        const jobId: string   = this.node.props?.jobId ?? '';

        try {
            // Fails here, with a message naming the missing key, rather than letting the backend
            // throw `Invalid templateUrl (must be a full http/https URL): undefined` at a crew (#72).
            await this._assertAegisConfigured(docType);

            const res = await this.apollo.mutate<any>({
                mutation: CREATE_DOCUMENT,
                variables: {
                    // Aegis only (#64). The navigator no longer creates PandaDoc documents in any
                    // zone. The *signing* branch below deliberately stays: it resolves its engine
                    // from the artifact's own tag, so contracts created on the desktop — of which
                    // both live franchises hold thousands, unsigned — can still be signed here.
                    serviceToUse: 'aegis',
                    documentsToSend: docType ? [docType] : [],
                    saveArtifact: true,
                    commentWithArtifactOn: jobId ? ['Job'] : [],
                    send: false,
                    variables: jobId ? { jobId } : undefined,
                },
                // Same zone the engine was resolved against, and for the same reason: the Aegis
                // branch of `createDocument` re-reads `standard-documents.<docType>` server-side to
                // get `templateUrl`. Resolved against the wrong zone that comes back without one,
                // and the render fails on an invalid template URL instead of using the franchise's.
                context: this._zoneContext(),
            }).toPromise();

            const created = res?.data?.createDocument?.[0];
            if (!created?.artifact?.id) throw new Error('No artifact returned');

            this.artifactId = created.artifact.id;
            this._applyArtifactData(created.artifact);
            this.state = 'ready';
        } catch (err: any) {
            console.error('[NavPandaDocSigner] generate() failed:', err);
            this.error = err?.message ?? 'Failed to generate document. Try again.';
            this.state = 'idle';
        } finally {
            this.generating = false;
            this._rebuildSteps();
        }
    }

    // ── Iframe ────────────────────────────────────────────────────────────────

    async openIframe(role: SignerRole) {
        if (this.loadingLink) return;
        this.error = '';
        this.activeRole = role;
        this.signingUrl = null;
        this.loadingLink = true;
        this.iframeLoading = true;
        this.iframeOpen = true;
        this.state = 'signing';

        try {
            const url = this.artifactEngine === 'aegis'
                ? await this._getAegisSigningLink(role)
                : await this._getPandadocSigningLink();

            if (!url) throw new Error('No signing link returned');
            // Requirement: #54 - only a surface that actually opened may unlock the re-check, and
            // through it the manual override. Set here rather than before the `try`: every throw
            // path below closes the iframe again, so a flag set on the way in would offer
            // "Check again" and then an override for a document nobody ever opened. Aegis makes
            // that reachable in one tap, since `_getAegisSigningLink` rejects an unsupported role
            // synchronously. Not reset in `catch` either — a later failed attempt must not erase
            // the flag an earlier successful one earned.
            this.signingOpened = true;
            this.signingUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        } catch (err: any) {
            const msg: string = err?.message ?? '';

            // PandaDoc-only recovery. Aegis mints a link from the artifact and the role alone, with
            // no recipient to resolve, so it has no equivalent failure — running this branch for it
            // would silently strip a role's signing button on an unrelated error.
            const noRecipient = this.artifactEngine === 'pandadoc' && (
                msg.includes('No employee recipient')
                || msg.includes('No customer recipient')
                || msg.includes('recipient found in metadata')
            );

            if (noRecipient) {
                // Flip the capability flag — next render shows Open+Done instead
                if (role === 'employee') this.capabilities.hasEmployee = false;
                if (role === 'customer') this.capabilities.hasCustomer = false;
                this.error = '';
            } else {
                this.error = msg || 'Could not open signing link. Check your connection.';
            }
            this.iframeOpen = false;
            this.state = 'ready';
            this._rebuildSteps();
        } finally {
            this.loadingLink = false;
        }
    }

    /** Use PandadocService — goes through the backend properly. */
    private async _getPandadocSigningLink(): Promise<string> {
        const res = await this.pandadocService.getDocumentLink({
            artifactId: this.artifactId,
            role: this.activeRole,
        }).toPromise();

        const url: string = (res?.data as any)?.getPandadocDocumentLink;

        // getPandadocDocumentLink silently sends the doc — it's at least 'sent' now.
        // Set this so closeIframe() knows to show the "Mark All Done" banner
        // even when the artifact still shows status::draft (webhook not yet received).
        //
        // Deliberately NOT done on the Aegis path: `getAegisDocumentLink` has no side effects, so
        // the document really is still a draft afterwards. Claiming otherwise would make the
        // "Check again" button — gated on `docStatus !== 'draft'` — appear for a document nothing
        // has happened to yet.
        if (url && (this.docStatus === 'draft' || this.docStatus === 'uploaded')) {
            this.docStatus = 'sent';
        }

        return url;
    }

    /**
     * Mint a role-scoped Aegis sign page.
     *
     * The link *is* the signing surface — Aegis has no view-only role — so opening it for a role is
     * the same act as letting that role sign. That is what the step wants: employee signs, hands the
     * device over, customer signs.
     *
     * Unlike PandaDoc's link this neither resolves a recipient nor sends anything, so it works while
     * the document is still a draft. That is why in-navigator signing needs no send step first, and
     * why it must not be given one — sending regenerates the document, which throws once signatures
     * exist.
     */
    private async _getAegisSigningLink(role: SignerRole): Promise<string> {
        if (!AEGIS_SIGNER_ROLES.includes(role)) {
            throw new Error(`Aegis cannot sign as '${role}' — supported roles are ${AEGIS_SIGNER_ROLES.join(' and ')}.`);
        }

        // No `_zoneContext()` here, deliberately: it would be inert. `aegis.getDocumentLink` looks
        // the artifact up by id with no `zone` in the filter — and the zone snippet emits nothing
        // when the filter carries no zone — then resolves the zone as
        // `artifact.metadata.zoneId || ctx.meta.zone`, and Aegis artifacts always carry that
        // metadata. Passing a header that provably changes nothing invites the next reader to treat
        // it as load-bearing and copy it.
        const res = await this.aegisLinkGQL.fetch(
            { artifactId: this.artifactId, role },
            { fetchPolicy: 'no-cache' },
        ).toPromise();

        return res?.data?.getAegisDocumentLink ?? '';
    }

    closeIframe() {
        this.iframeOpen = false;
        this.signingUrl = null;
        this.iframeLoading = false;
        this.loadingLink = false;
        this.state = 'ready';
        this._pollArtifactStatus();
    }

    /**
     * Mark a role satisfied **without** a signature from PandaDoc.
     *
     * Only legitimate for a role with no signature field on the document (`!hasRecipient`), where
     * there is nothing for PandaDoc to report and the crew confirming they showed it is the whole
     * interaction. The template offers it only in that branch, labelled "Reviewed".
     *
     * It used to be offered next to "Sign Now" for roles that **do** have a signature field, as a
     * webhook-failure override. That made it a one-tap way past a customer signature: `isSatisfied`
     * requires every signer role to be in `signedRoles`, and this writes into that set with nothing
     * checked, so the gate was satisfied by an assertion rather than a fact. Worse, it is local-only
     * — nothing here persists `signedRoles` — so a reload re-derived them from artifact metadata and
     * silently re-blocked the step, leaving no trace of the override.
     *
     * That case now offers `recheckSignature()` instead, which asks PandaDoc rather than telling it.
     * A deliberate, audited override for a genuinely stuck webhook is still wanted — see the
     * write-up in `docs/crew-navigator.md`.
     */
    markSigned(role: SignerRole) {
        this.signedRoles.add(role);
        this._checkComplete();
        this._rebuildSteps();
        this._notifyReady();
    }

    /**
     * Re-query the artifact's real signature status.
     *
     * The button this replaces existed because the signing webhook does not always land. This
     * addresses that same complaint honestly: it re-reads the artifact and lets `_applyArtifactData`
     * decide, so a signature that exists is picked up and one that does not stays missing.
     *
     * A re-check that comes back empty is also what *unlocks the override* below — the crew has to
     * ask the system before they may overrule it.
     */
    async recheckSignature(role: SignerRole) {
        // Only a re-check that actually reached the server may unlock the override. `_pollArtifactStatus`
        // returns early when there is no artifact yet or a poll is already running, and treating that
        // silence as "we asked and found nothing" would let the override open on a no-op.
        const didQuery = await this._pollArtifactStatus();
        if (!didQuery) { return; }

        // Scoped to the row the crew re-checked. Marking every unsigned role meant one re-check on
        // the employee row also unlocked the customer's override — the signature that matters most.
        if (!this.signedRoles.has(role)) { this.recheckedWithoutSignature.add(role); }
    }

    /**
     * Whether the "confirm it was signed" affordance may be shown for a role.
     *
     * Gated on a re-check having already run and found nothing, so the override cannot be reached by
     * a stray tap — which is what the old "✓ Done" button was, sitting as a same-size peer of
     * "Sign Now" with no confirmation (#54).
     */
    canOfferOverride(role: SignerRole): boolean {
        return this.recheckedWithoutSignature.has(role)
            && !this.signedRoles.has(role)
            && !this.pollingStatus;
    }

    askOverride(role: SignerRole) { this.overrideConfirmingRole = role; }

    cancelOverride() { this.overrideConfirmingRole = null; }

    /**
     * Record that the crew asserts this role signed, despite PandaDoc not reporting it.
     *
     * A genuine escape hatch: the signing webhook does fail, and a crew standing in a driveway with a
     * customer who *has* signed must be able to continue. It is deliberately two taps behind a
     * re-check, worded so the crew knows they are asserting on someone else's behalf, and marked in
     * the UI so an asserted signature never reads as a verified one.
     *
     * **Still incomplete, and tracked as #54.** This does not persist — nothing here writes
     * `signedRoles` to `navigatorData`, so a reload re-derives them from artifact metadata and the
     * step re-blocks — and it does not record *who* overrode it. Both need the actor work in **P4**.
     */
    confirmOverride(role: SignerRole) {
        this.manuallyConfirmedRoles.add(role);
        this.overrideConfirmingRole = null;
        this.markSigned(role);
    }



    skip() {
        this.skipped = true;
        this._notifyReady();
    }

    // ── Polling ───────────────────────────────────────────────────────────────

    /**
     * Returns whether a query actually ran. Callers that gate on "we asked the server and it said
     * no" must not treat an early return as an answer — see `recheckSignature`.
     */
    private async _pollArtifactStatus(): Promise<boolean> {
        if (!this.artifactId || this.pollingStatus) return false;
        this.pollingStatus = true;
        await new Promise(r => setTimeout(r, 800));
        try {
            const res = await this.apollo.query<any>({
                query: GET_ARTIFACT_STATUS,
                variables: { artifactId: this.artifactId },
                fetchPolicy: 'no-cache',
                context: this._zoneContext(),
            }).toPromise();
            const artifact = res?.data?.artifacts?.artifacts?.[0];
            if (artifact) this._applyArtifactData(artifact);
            this._checkComplete();
            this._rebuildSteps();
            return true;
        } catch { return false; /* non-fatal */ }
        finally { this.pollingStatus = false; }
    }

    // ── Completion ────────────────────────────────────────────────────────────

    private _checkComplete() {
        if (this.signerRoles.every(r => this.signedRoles.has(r))) {
            this.completeMessage = this.signerRoles.length === 1
                ? `Signed by ${this.roleLabel(this.signerRoles[0])}`
                : 'Signed by all parties';
            this.state = 'complete';
            this.iframeOpen = false;
            this._rebuildSteps();
        }
        this._notifyReady();
    }

    private _notifyReady() {
        if (typeof this.node.props?.onReady === 'function') {
            this.node.props.onReady(this.isSatisfied);
        }
    }
}
